import React, { useState, useEffect, useContext } from "react";
import { AuthProvider, AuthContext } from "./contexts/AuthContext";
import { I18nextProvider } from 'react-i18next';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import Layout from './components/Shared/Layout';
import IdleTimer from './components/Shared/IdleTimer';
import EnvironmentBanner from './components/Shared/EnvironmentBanner';
import LocalDevBanner from './components/Shared/LocalDevBanner';
import { FeatureFlags as FeatureFlagProvider } from "./components/Shared/FeatureFlags";
import { EnvironmentProvider } from "./contexts/EnvironmentContext";
import MFAPrompt from './components/Auth/MFAPrompt';
import i18n from "./i18n.js";
import { AppRoutes } from "./routes.jsx";
import { Toaster } from 'react-hot-toast';
import CustomToast from './toast/CustomToast.jsx';
import ErrorBoundary from './components/Shared/ErrorBoundary';
import { ThemeProvider } from './contexts/ThemeContext';
import { initOfflineSync } from './services/offline/offlineQueue';

const queryClient = new QueryClient();

// Global password change enforcement component
function PasswordEnforcer({ children }) {
  const { user, loading } = useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();
  
  // Paths exempt from password change requirement
  const exemptPaths = ['/login', '/forgot-password', '/reset-password', '/change-password', '/session-expired', '/home', '/server-error', '/unknown-error'];
  const isExemptPath = exemptPaths.some(path => location.pathname.startsWith(path));
  
  useEffect(() => {
    if (!loading && user?.requires_password_change && !isExemptPath) {
      console.log('[ENFORCER] Redirecting to password change from', location.pathname);
      navigate('/change-password', { state: { from: location.pathname }, replace: true });
    }
  }, [user, loading, location.pathname, navigate, isExemptPath]);
  
  return children;
}

function AppContent() {
  const [loadingOperations, setLoadingOperations] = useState(0);
  const [initialLoading, setInitialLoading] = useState(false);
  const isDevEnvironment = process.env.NODE_ENV === 'development';
  const isProd = process.env.NODE_ENV === 'production';
  const { userType, loading, user } = useContext(AuthContext);
  const location = useLocation();

  // FECOA-1270: participant offline retry queue — flush on reconnect ('online'
  // event), on load, and on a periodic retry. No-op for staff sessions.
  useEffect(() => initOfflineSync(), []);
  
  // Allow development tools for superusers in any environment
  const isDev = user?.is_superuser;
  
  // Log when dev tools are enabled for debugging
  useEffect(() => {
    if (isDev && user?.is_superuser) {
      console.debug(`Development tools enabled for superuser in ${process.env.NODE_ENV || 'unknown'} environment`);
    }
  }, [isDev, user]);

  useEffect(() => {
    if (loadingOperations > 0 && !initialLoading) {
      setInitialLoading(true);
    } else if (loadingOperations === 0 && initialLoading) {
      setInitialLoading(false);
    }
  }, [loadingOperations, initialLoading]);

  if (loading) {
    return <div>Loading...</div>;
  }

  // Caregiver-facing routes use a stripped-down chrome (no env banner with sponsor name,
  // no breadcrumbs, no sidebar). They render their own minimal pages.
  const isCaregiverRoute =
    location.pathname.startsWith('/caregiver-login') ||
    location.pathname.startsWith('/caregiver/');

  return (
    <PasswordEnforcer>
      {!isCaregiverRoute && (
        <EnvironmentBanner participantMode={userType === 'participant'} />
      )}
      <Layout isDev={isDev} userType={userType}>
        <IdleTimer />
        <I18nextProvider i18n={i18n}>
          {loadingOperations > 0 || initialLoading ? (
            <div></div>
          ) : (
            <>
              <motion.div
                key={location.pathname}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
              >
                <AppRoutes isDev={isDev} />
              </motion.div>
              <MFAPrompt />
              <Toaster 
                position="top-right"
                reverseOrder={true}
                gutter={8}
                containerStyle={{
                  top: 20,
                  right: 20,
                  zIndex: 99999,
                }}
                toastOptions={{
                  duration: 5000, 
                  style: {
                    background: 'transparent',
                    padding: 0,
                    margin: 0,
                    boxShadow: 'none',
                    border: 'none',
                    maxWidth: '420px',
                    width: '100%',
                  },
                  success: {
                    duration: 5000,
                  },
                  error: {
                    duration: 8000, 
                  },
                  loading: {
                    duration: Infinity,
                  },
                }}
              >
                {(t) => (
                  <CustomToast t={t} />
                )}
              </Toaster>
            </>
          )}
        </I18nextProvider>
      </Layout>
    </PasswordEnforcer>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <LocalDevBanner />
        <AuthProvider>
          <QueryClientProvider client={queryClient}>
            <FeatureFlagProvider>
              <EnvironmentProvider>
                <AppContent />
              </EnvironmentProvider>
            </FeatureFlagProvider>
          </QueryClientProvider>
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;